import React, { useState, useEffect } from 'react';
import { ComposedChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { getCompanyInfo, getQuarterly, getMonthly, getAnnual, getDividend, getEstEps, addWatchlist, removeWatchlist, getWatchlist } from '../api';

import AILoadingIndicator from '../components/AILoadingIndicator';
const API_URL = 'https://stock-api-production-913a.up.railway.app';

export default function StockOverview({ ticker }) {
  // 手機偵測
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [info, setInfo] = useState(null);
  const [quarterly, setQuarterly] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [annual, setAnnual] = useState([]);
  const [dividend, setDividend] = useState([]);
  const [shareholding, setShareholding] = useState([]);
  // [ADMIN_EXTRA_METRICS_PATCHED] 管理員專屬擴充指標
  const [extMetrics, setExtMetrics] = useState(null);
  const [estEps, setEstEps] = useState(null);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [closePrice, setClosePrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState({});
  const [priceOHLCV, setPriceOHLCV] = useState([]);
  const [chipData, setChipData] = useState([]);
  const [aiReport, setAiReport] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [reportDate, setReportDate] = useState('');
  const [aiFinReport, setAiFinReport] = useState('');
  const [aiFinLoading, setAiFinLoading] = useState(false);
  const reportDateRef = React.useRef('');

  // AI報告文字轉HTML（narrative、financial 兩段共用同一套渲染規則）
  const renderAiHtml = (txt) => (txt || '')
    .replace(/^#### (.+)$/gm, '<h4 style="color:#7ec8e3;margin:16px 0 4px">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 style="color:#4C9BB8;margin:20px 0 6px">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="color:#4C9BB8;border-bottom:1px solid #1a3a5a;padding-bottom:6px;margin:24px 0 8px">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 style="color:#4C9BB8;margin:24px 0 8px">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#eee">$1</strong>')
    .replace(/^> (.+)$/gm, '<blockquote style="border-left:3px solid #4C9BB8;padding:6px 12px;background:#0a1828;margin:8px 0;color:#90b8d8">$1</blockquote>')
    .replace(/^(\|.+\|)$/gm, (row) => {
      if (row.match(/^\|[-| ]+\|$/)) return '';
      const cells = row.split('|').filter((c,i,a)=>i>0&&i<a.length-1);
      return '<tr>' + cells.map(c=>`<td style="border:1px solid #1e3a5a;padding:4px 10px;color:#b8d4e8">${c.trim()}</td>`).join('') + '</tr>';
    })
    .replace(/(<tr>.*<\/tr>\n?)+/gs, m => `<table style="border-collapse:collapse;width:100%;margin:10px 0;font-size:13px">${m}</table>`)
    .replace(/^---$/gm, '<hr style="border-color:#1a3a5a;margin:16px 0"/>')
    .replace(/\n/g, '<br/>');

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    setClosePrice(null);
    setAiReport('');
    setChipData([]);   // 切換股票時立刻清空,避免後端 500 時殘留前一檔資料
    Promise.all([
      getCompanyInfo(ticker).catch(() => null),
      getQuarterly(ticker).catch(() => []),
      getMonthly(ticker).catch(() => []),
      getAnnual(ticker).catch(() => []),
      getDividend(ticker).catch(() => []),
      getEstEps(ticker).catch(() => null),
      fetch(`${API_URL}/api/company/${ticker}/shareholding?weeks=52`).then(r=>r.json()).catch(()=>[]),
    ]).then(([i, q, m, a, d, e, sh]) => {
      setInfo(i); setQuarterly(q); setMonthly(m);
      setAnnual(a); setDividend(d); setEstEps(e);
      setShareholding(Array.isArray(sh) ? sh : []);
      setLoading(false);
    });
    // 擴充指標 (基本欄位開放訪客；毛利率迴歸前端額外用 isAdmin 過濾) // 月佔季比已加
    fetch(`${API_URL}/api/company/${ticker}/extended-metrics`)
      .then(r => r.json())
      .then(d => setExtMetrics(d && !d.error ? d : null))
      .catch(() => setExtMetrics(null));
    // [WL_OVERVIEW_PATCHED] 查此股是否在私房股 - 管理員看後端, 訪客看 localStorage
    {
      const _token = sessionStorage.getItem('wintime_admin_token') || '';
      const isAdmin = !!_token;
      if (isAdmin) {
        fetch(`${API_URL}/api/watchlist`)
          .then(r => r.json()).then(wl => {
            setInWatchlist(Array.isArray(wl) && wl.some(w => w.ticker === ticker));
          }).catch(() => {});
      } else {
        try {
          const stored = localStorage.getItem('wt_watchlist');
          const wl = stored ? JSON.parse(stored) : [];
          setInWatchlist(Array.isArray(wl) && wl.some(w => w.ticker === ticker));
        } catch { setInWatchlist(false); }
      }
    }
    fetch(`${API_URL}/api/company/${ticker}/price`)
      .then(r=>r.json()).then(arr => {
        const map = {};
        (arr||[]).forEach(p => { map[p.date] = p.close; });
        setPriceHistory(map);
        setPriceOHLCV(arr || []);
      }).catch(()=>{});
    fetch(`${API_URL}/api/company/${ticker}/chip-compass`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setChipData(Array.isArray(d) ? d : []))
      .catch(() => setChipData([]));
    fetch(`${API_URL}/api/company/${ticker}/close-price`)
      .then(r => r.json())
      .then(d => { if (d.close) setClosePrice(d); })
      .catch(() => {});

  }, [ticker]);

  if (!ticker) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📈</div>
      <div style={{ fontSize: 18 }}>請從左側輸入股票代號或名稱</div>
    </div>
  );
  if (loading) return <div className="loading">⏳ 載入中...</div>;
  if (!info) return <div className="loading">找不到股票資料</div>;

  const qData = [...quarterly].sort((a, b) => b['期別'] > a['期別'] ? 1 : -1);
  const qDataAsc = [...quarterly].sort((a, b) => a['期別'] > b['期別'] ? 1 : -1);
  const qHeaders = qData.map(r => r['期別']);
  const mData = [...monthly].sort((a, b) => b.period > a.period ? 1 : -1);
  const aData = [...annual].sort((a, b) => b.year > a.year ? 1 : -1);

  const fmt = (v, dec=2) => (v === '' || v == null || isNaN(Number(v))) ? '-' : Number(v).toFixed(dec);
  const fmtN = (v, dec=2) => (v === '' || v == null || isNaN(Number(v))) ? '-' : Number(v).toFixed(dec);
  const colorPos = (v) => (v === '' || v == null || isNaN(Number(v))) ? '#ccc' : Number(v) > 0 ? '#d62828' : Number(v) < 0 ? '#00a14b' : '#ccc';
  const colorNeg = (v) => (v === '' || v == null || isNaN(Number(v))) ? '#ccc' : Number(v) > 0 ? '#00a14b' : Number(v) < 0 ? '#d62828' : '#ccc';

  const sectionTitle = (title) => (
    <div style={{ background: '#1a2a3a', color: '#4C9BB8', padding: '8px 12px', fontWeight: 700, fontSize: 15, marginTop: 24, marginBottom: 4, borderLeft: '4px solid #4C9BB8' }}>
      {title}
    </div>
  );

  const renderQTable = (rows) => (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 11 : 13 }}>
        <thead>
          <tr style={{ background: '#070a0f' }}>
            <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, background: '#070a0f', minWidth: isMobile ? 130 : 220 }}>項目</th>
            {qHeaders.map(h => <th key={h} style={th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#080b10' : '#070a0e' }}>
              <td style={{ ...td, textAlign: 'left', color: '#90c0dc', fontWeight: 600, position: 'sticky', left: 0, zIndex: 1, background: '#080b10', ...(row.sep ? { borderBottom: '2px solid #ffffff' } : {}) }}>{row.label}</td>
              {qData.map(q => {
                const val = row.calc ? row.calc(q) : (row.key ? q[row.key] : null);
                const sepStyle = row.sep ? { borderBottom: '2px solid #ffffff' } : {};
                return (
                  <td key={q['期別']} style={{ ...td, ...sepStyle, color: row.cf ? row.cf(val) : '#555' }}>
                    {fmt(val)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderATable = (rows) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#070a0f' }}>
            <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, background: '#070a0f', minWidth: 220 }}>項目</th>
            {aData.map(r => <th key={r.year} style={th}>{r.year}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#080b10' : '#070a0e' }}>
              <td style={{ ...td, textAlign: 'left', color: '#8ab8d0', fontWeight: 500, position: 'sticky', left: 0, zIndex: 1, background: '#080b10', ...(row.sep ? { borderBottom: '2px solid #ffffff' } : {}) }}>{row.label}</td>
              {aData.map(r => {
                const val = row.calc ? row.calc(r) : (row.key ? r[row.key] : null);
                const sepStyle = row.sep ? { borderBottom: '2px solid #ffffff' } : {};
                return (
                  <td key={r.year} style={{ ...td, ...sepStyle, color: row.cf ? row.cf(val) : '#555' }}>
                    {fmt(val)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '4px' : '8px' }}>
      {/* 標題 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ fontSize: isMobile ? 18 : 24, fontWeight: 700, color: '#ef4444', marginBottom: 4 }}>
            {ticker} {info.name}
            {closePrice && <span style={{ fontSize: isMobile ? 14 : 26, color: '#22c55e', marginLeft: isMobile ? 8 : 48, fontWeight: 400 }}>
              <span style={{ color: '#7a9ab8', fontSize: 13, marginRight: 6 }}>收盤價</span><b style={{ color: '#22c55e', fontSize: isMobile ? 28 : 42, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace" }}>{closePrice.close}</b>{closePrice.change != null && (<span style={{ marginLeft: 12, color: closePrice.change >= 0 ? '#d62828' : '#00a14b', fontSize: isMobile ? 14 : 18, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, verticalAlign: 'middle' }}>{closePrice.change >= 0 ? '▲' : '▼'} {Math.abs(closePrice.change).toFixed(2)} ({closePrice.change_pct >= 0 ? '+' : ''}{closePrice.change_pct.toFixed(2)}%)</span>)}
              <span style={{ fontSize: isMobile ? 13 : 18, color: '#22c55e', marginLeft: 12 }}>{closePrice.date}</span>
            </span>}
          </h2>
          {info.sub_industry && <span style={{ background: '#2a3a4a', color: '#4C9BB8', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{info.sub_industry}</span>}
          {info.product_mix && (
            <div style={{ color: '#90c8e8', fontSize: 11, marginTop: 4, lineHeight: 1.7, maxWidth: isMobile ? '100%' : 900 }}>
              【{info.product_mix}】
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : undefined }}>
          <button onClick={async () => {
              setAiLoading(true); setAiReport('');
              setAiFinReport(''); // 換股票/重新查詢時，財務部份也要清空，避免對錯股票
              const d=new Date();
              const ds=`${d.getFullYear()}年${String(d.getMonth()+1).padStart(2,'0')}月${String(d.getDate()).padStart(2,'0')}日`;
              reportDateRef.current = ds;
              setReportDate(ds);

              // 24小時快取：同一支股票當天不重複呼叫AI
              const cacheKey = `wintime_ai_narrative_${ticker}_${d.getFullYear()}${d.getMonth()}${d.getDate()}`;
              try {
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                  const { data: cachedData, ts } = JSON.parse(cached);
                  if (Date.now() - ts < 86400000) { // 24小時
                    setAiReport(cachedData);
                    setAiLoading(false);
                    return;
                  }
                }
              } catch {}

              try {
                // [AI_DAILY_LIMIT_PATCHED] 管理員帶 ?admin=1 跳過每日上限
                const _isAdmin = !!sessionStorage.getItem('wintime_admin_token');
                const _aiUrl = `${API_URL}/api/company/${ticker}/analysis-narrative${_isAdmin ? '?admin=1' : ''}`;
                const res = await fetch(_aiUrl, { method: 'POST' });
                const data = await res.json();
                const report = data.ok ? data.report : ('錯誤：' + data.error);
                setAiReport(report);
                // 存入快取
                try { localStorage.setItem(cacheKey, JSON.stringify({ data: report, ts: Date.now() })); } catch {}
              } catch(e) { setAiReport('連線失敗：' + e.message); }
              setAiLoading(false);
            }}
            disabled={aiLoading}
            style={{ padding: isMobile ? '10px 8px' : '8px 20px', borderRadius: 8, border: '1px solid #f5c518', background: aiLoading ? '#1a2a3c' : 'linear-gradient(135deg,#2e5872,#4080a0)', color: '#eef8ff', cursor: aiLoading ? 'default' : 'pointer', fontWeight: 700, whiteSpace: 'nowrap', fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2, flex: isMobile ? 1 : undefined, fontSize: isMobile ? 13 : undefined }}>
            {aiLoading ? '⏳ 分析中...' : '🤖 AI個股分析'}
          </button>
          <button onClick={() => {
            // 立即更新畫面（optimistic update）
            const next = !inWatchlist;
            setInWatchlist(next);
            // 背景處理 - 依角色決定寫後端還是 localStorage
            const _token = sessionStorage.getItem('wintime_admin_token') || '';
      const isAdmin = !!_token;
            const stockName = info?.name || '';
            if (isAdmin) {
              // 管理員: 寫後端 DB
              if (!inWatchlist) {
                fetch(`${API_URL}/api/watchlist/${ticker}?name=${encodeURIComponent(stockName)}&token=${encodeURIComponent(_token)}`, { method: 'POST' })
                  .then(r => r.json())
                  .then(d => { if (!d.ok) setInWatchlist(false); else window.dispatchEvent(new CustomEvent('watchlistChanged', { detail: { action:'add', ticker, name: stockName } })); })
                  .catch(() => setInWatchlist(false));
              } else {
                fetch(`${API_URL}/api/watchlist/${ticker}?token=${encodeURIComponent(_token)}`, { method: 'DELETE' })
                  .then(() => window.dispatchEvent(new CustomEvent('watchlistChanged', { detail: { action:'remove', ticker } })))
                  .catch(() => setInWatchlist(true));
              }
            } else {
              // 訪客: 寫 localStorage (與 Sidebar 共用 wt_watchlist key)
              try {
                const stored = localStorage.getItem('wt_watchlist');
                const wl = stored ? JSON.parse(stored) : [];
                const filtered = wl.filter(w => w.ticker !== ticker);
                const updated = !inWatchlist ? [...filtered, { ticker, name: stockName }] : filtered;
                localStorage.setItem('wt_watchlist', JSON.stringify(updated));
                window.dispatchEvent(new CustomEvent('watchlistChanged', { detail: { action: !inWatchlist ? 'add' : 'remove', ticker, name: stockName } }));
              } catch {}
            }
          }}
          style={{ padding: isMobile ? '10px 8px' : '8px 20px', borderRadius: 8, border: '1px solid #2a4060',
            background: inWatchlist ? '#1e3a52' : 'transparent',
            color: inWatchlist ? '#90c0dc' : '#6a98b8', cursor: 'pointer', fontWeight: 600,
            transition: 'all 0.15s', flex: isMobile ? 1 : undefined, fontSize: isMobile ? 13 : undefined, whiteSpace: 'nowrap' }}>
            {inWatchlist ? '★ 已追蹤' : '☆ 加入追蹤'}
          </button>
        </div>
      </div>

      {/* 財務摘要列 */}
      {(() => {
        const q0 = qData[0] || {};
        const q4eps = qData.slice(0,4).reduce((s,q) => s + (Number(q['每股盈餘']) || 0), 0);
        const te = q0['股東權益_億']; const cs = q0['股本_億'];
        const bv = (te && cs && cs !== 0) ? te / cs * 10 : null;
        const cp = closePrice ? Number(closePrice.close) : null;
        const pe = (cp && q4eps && q4eps > 0) ? (cp / q4eps).toFixed(1) : null;
        const pb = (cp && bv) ? (cp / bv).toFixed(2) : null;
        const m0 = mData[0] || {};

        const C = ({ label, value, color, big, small }) => (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: isMobile ? '6px 4px' : '8px 12px', borderRight: '1px solid #0f1c2a', minWidth: isMobile ? 0 : 90 }}>
            <span style={{ color: '#8ab8d0', fontSize: 9, marginBottom: 4, whiteSpace: 'nowrap', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: "'Rajdhani',sans-serif", fontWeight: 600 }}>{label}</span>
            <span style={{ color: color || '#8ab0cc', fontWeight: big ? 600 : 500, fontSize: small ? 11 : big ? 16 : 14, whiteSpace: 'nowrap', fontFamily: "'JetBrains Mono',monospace" }}>{value ?? '-'}</span>
          </div>
        );

        return (
          <div style={{ background: '#080b10', borderRadius: 0, marginBottom: 1, overflow: 'hidden', borderBottom: '1px solid #1a2a3c' }}>
            {/* 第一行：現況 */}
            <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, flexWrap: 'wrap', borderBottom: '1px solid #2a3a4a', padding: '4px 0' }}>
              <C label="最近4季EPS" value={q4eps ? q4eps.toFixed(2) : '-'} color="#c0e0f8" big />
              <C label="最近1季EPS" value={q0['每股盈餘'] != null && q0['每股盈餘'] !== '' ? Number(q0['每股盈餘']).toFixed(2) : '-'} color="#c0e0f8" big />
              <C label="最近1季毛利率" value={q0['毛利率'] != null ? q0['毛利率']+'%' : '-'} color="#d62828" />
              <C label="最近1季營益率" value={q0['營益率'] != null ? q0['營益率']+'%' : '-'} color="#d62828" />
              <C label="最近1季營收年增率" value={q0['營收年增率'] != null ? q0['營收年增率']+'%' : '-'} color={Number(q0['營收年增率']) >= 0 ? '#d62828' : '#00a14b'} />
              <C label="最近1月營收年增率" value={m0.yoy_pct != null ? Number(m0.yoy_pct).toFixed(1)+'%' : '-'} color={Number(m0.yoy_pct) >= 0 ? '#d62828' : '#00a14b'} />
              <C label="目前本益比" value={pe ? pe+'x' : '-'} color="#c8a200" />
              <C label="股價淨值比" value={pb ? pb+'x' : '-'} color="#c8a200" />
              {/* [ADMIN_EXTRA_METRICS_PATCHED] 管理員專屬: 最近1季營收/最近1月營收/毛利率迴歸 */}
              {extMetrics && (<>
                <C label="最近1季營收(億)" value={extMetrics.latest_q_revenue != null ? Number(extMetrics.latest_q_revenue).toLocaleString() : '-'} color="#90d8b0" />
                {(() => {
                  const p = String((mData[0] || {}).period || '');
                  const mm = p.includes('-') ? parseInt(p.split('-')[1], 10)
                    : p.includes('/') ? parseInt(p.split('/').pop(), 10)
                    : parseInt(p.slice(-2), 10);
                  const N = mm ? ((mm - 1) % 3) + 1 : 1;
                  const cumRev = mData.slice(0, N).reduce((s, r) => s + (Number(r.revenue) || 0), 0) / 100000;
                  const q = Number(extMetrics.latest_q_revenue);
                  const pct = q > 0 ? cumRev / q * 100 : null;
                  const threshold = N / 3 * 100;
                  return (
                    <C label={`最近${N}月營收(億)`}
                      value={cumRev > 0 ? (
                        <span>{cumRev.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' '}
                          {pct != null && <span style={{ color: pct > threshold ? '#d62828' : '#00a14b', fontSize: 11 }}>{`(${pct.toFixed(0)}%)`}</span>}
                        </span>
                      ) : '-'}
                      color="#90d8b0" />
                  );
                })()}
                {!!sessionStorage.getItem('wintime_admin_token') && (
                  <C label="毛利率(迴歸)" value={extMetrics.gross_rate_regression ?? '-'} color="#90d8b0" small={typeof extMetrics.gross_rate_regression === 'string' && extMetrics.gross_rate_regression.length > 8} />
                )}
              </>)}
            </div>
            {/* 第二行：預估 */}
            {estEps && (
              <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, flexWrap: 'wrap', padding: '4px 0', background: '#060910' }}>
                <div style={{ color: '#7aaac8', fontSize: 9, padding: '8px 12px', display: 'flex', alignItems: 'center', fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, letterSpacing: 2, gridColumn: isMobile ? 'span 2' : undefined }}>🔮 預估</div>
                <C label="預估未來1季毛利率" value={estEps.est_gpm != null ? estEps.est_gpm+'%' : '-'} color="#7ec8e3" />
                <C label="預估未來1季營益率" value={estEps.est_opi != null ? estEps.est_opi+'%' : '-'} color="#7ec8e3" />
                <C label="預估未來1季EPS" value={estEps.non_op_flag ? '業外比重偏高，不宜估算' : (estEps.est_eps ?? '-')} color={estEps.non_op_flag ? '#f5a623' : '#c0e0f8'} big={!estEps.non_op_flag} small={estEps.non_op_flag} />
                <C label="預估未來4季EPS" value={estEps.non_op_flag ? '-' : (estEps.est_eps_4q ?? '-')} color="#c0e0f8" big />
                {(() => {
                  const eps3q = qData.slice(1,4).reduce((s,q) => s+(Number(q['每股盈餘'])||0), 0);
                  const base1q = eps3q + (estEps.est_eps || 0);
                  const pe1q = (!estEps.non_op_flag && cp && base1q > 0) ? (cp/base1q).toFixed(1)+'x' : '-';
                  const pe4q = (!estEps.non_op_flag && cp && estEps.est_eps_4q > 0) ? (cp/estEps.est_eps_4q).toFixed(1)+'x' : '-';
                  return (<>
                    <C label="預估未來1季本益比" value={pe1q} color="#c8a200" />
                    <C label="預估未來4季本益比" value={pe4q} color="#c8a200" />
                  </>);
                })()}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 16px', flex: 1, gridColumn: isMobile ? 'span 2' : undefined }}>
                  <span style={{ color: '#8ab8d0', fontSize: 9, lineHeight: 1.5 }}>
                    ⚠️ 本預估數據係依財務數據邏輯推演，僅供投資參考。<br/>預估結果不代表實際績效，投資人應審慎判斷，自負盈虧。
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })()}


      {/* ── AI 分析報告 ── */}
      {(aiReport || aiLoading) && (
        <div style={{ background: '#070a0f', border: '1px solid #1a2a3c', borderRadius: 0, padding: '20px 24px', marginBottom: 1 }}>
          {(() => {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth()+1).padStart(2,'0');
            const d = String(now.getDate()).padStart(2,'0');
            return (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
                <span style={{ color: '#a8d0e8', fontWeight: 700, fontSize: 15, fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2 }}>🤖 AI個股分析報告</span>
                <span style={{ color: '#6a98b8', fontSize: 12 }}>（報告日期：{y}年{m}月{d}日）</span>
              </div>
            );
          })()}
          {aiLoading ? (
            <AILoadingIndicator
              label="AI 分析中"
              estimatedSeconds={30}
              stages={['讀取公司基本資料', '搜尋近期市場消息', '分析營運趨勢', '撰寫分析師觀點']}
            />
          ) : (
            <div style={{ color: '#b8d4e8', fontSize: 15, lineHeight: 2.0 }}
              dangerouslySetInnerHTML={{ __html: renderAiHtml(aiReport) }}
            />
          )}
          {!aiLoading && aiReport && (
            <>
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#1a1a0a', border: '1px solid #4a4a20', borderRadius: 6, color: '#a0a060', fontSize: 12 }}>
                ⚠️ AI報告可能會有錯誤，請使用者小心謹慎核實數字後再做判斷。
              </div>

              {/* ── 財務部份：選擇性顯示，點擊才呼叫AI，避免每次都花錢產生 ── */}
              {!aiFinReport && !aiFinLoading && (
                <button onClick={async () => {
                    setAiFinLoading(true);
                    const d=new Date();
                    const cacheKey = `wintime_ai_financial_${ticker}_${d.getFullYear()}${d.getMonth()}${d.getDate()}`;
                    try {
                      const cached = localStorage.getItem(cacheKey);
                      if (cached) {
                        const { data: cachedData, ts } = JSON.parse(cached);
                        if (Date.now() - ts < 86400000) {
                          setAiFinReport(cachedData);
                          setAiFinLoading(false);
                          return;
                        }
                      }
                    } catch {}
                    try {
                      const _isAdmin = !!sessionStorage.getItem('wintime_admin_token');
                      const _finUrl = `${API_URL}/api/company/${ticker}/analysis-financial${_isAdmin ? '?admin=1' : ''}`;
                      const res = await fetch(_finUrl, { method: 'POST' });
                      const data = await res.json();
                      const report = data.ok ? data.report : ('錯誤：' + data.error);
                      setAiFinReport(report);
                      try { localStorage.setItem(cacheKey, JSON.stringify({ data: report, ts: Date.now() })); } catch {}
                    } catch(e) { setAiFinReport('連線失敗：' + e.message); }
                    setAiFinLoading(false);
                  }}
                  style={{ marginTop: 14, padding: '8px 20px', borderRadius: 8, border: '1px solid #4C9BB8', background: 'transparent', color: '#7ec8e3', cursor: 'pointer', fontWeight: 700, fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2 }}>
                  📊 顯示財務部份（獲利能力／營收動能／現金流／資產配置／股利／風險）
                </button>
              )}
              {aiFinLoading && (
                <div style={{ marginTop: 14 }}>
                  <AILoadingIndicator
                    label="財務深度分析中"
                    estimatedSeconds={45}
                    stages={['讀取8季財務資料', '套用私房分析規則', '分析資產配置', '撰寫財務深度分析']}
                  />
                </div>
              )}
              {!aiFinLoading && aiFinReport && (
                <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #1a2a3c' }}>
                  <div style={{ color: '#a8d0e8', fontWeight: 700, fontSize: 15, fontFamily: "'Rajdhani',sans-serif", letterSpacing: 2, marginBottom: 14 }}>📊 財務部份</div>
                  <div style={{ color: '#b8d4e8', fontSize: 15, lineHeight: 2.0 }}
                    dangerouslySetInnerHTML={{ __html: renderAiHtml(aiFinReport) }}
                  />
                </div>
              )}

              <button onClick={() => { setAiReport(''); setAiFinReport(''); }}
                style={{ marginTop: 14, padding: '4px 14px', borderRadius: 6, border: '1px solid #555', background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 12 }}>
                ✕ 關閉報告
              </button>
            </>
          )}
        </div>
      )}

      {/* ── K 線圖 ── */}
      {priceOHLCV.length > 0 && (() => {
        const cutoff = new Date();
        cutoff.setMonth(cutoff.getMonth() - 14);
        const price14m = priceOHLCV.filter(d => new Date(d.date) >= cutoff);
        return (
          <div style={{ background: '#080b10', border: '1px solid #1a2a3c', marginBottom: 16, padding: '12px 16px' }}>
            {sectionTitle('📊 近14個月股價走勢（K線 + 均線 + 量）')}
            <KLineChart data={price14m} />
          </div>
        );
      })()}

      {/* ── 圖表區 ── */}
      {qData.length > 0 && (() => {
        // 圖表資料：由舊到新
        const chartData = [...qData].reverse().map(q => ({
          期別: q['期別'],
          營業收入: q['營業收入_億'],
          毛利率: q['毛利率'],
          營益率: q['營益率'],
          稅前淨利率: q['稅前淨利率'],
          推銷費用率: q['推銷費用率'],
          管理費用率: q['管理費用率'],
          研發費用率: q['研發費用率'],
          不透明資產: (() => {
            const a = q['備供出售流動_億'] || 0;
            const b = q['備供出售非流動_億'] || 0;
            const c = q['採權益法投資_億'] || 0;
            const d = q['無形資產_億'] || 0;
            return +(a+b+c+d).toFixed(2);
          })(),
          財務透明度: q['財務透明度'],
          稅前淨利億: q['稅前淨利_億'] ?? null,
          營業CF億: q['營業CF_億'] ?? null,
          餘絀CF億: q['餘絀CF_億'] ?? null,
          有息負債億: q['有息負債_億'] ?? null,
          存貨億: q['存貨_億'] ?? null,
          不動產設備億: q['不動產廠房設備_億'] ?? null,
          無形資產億: q['無形資產_億'] ?? null,
        }));

        const chartStyle = { background: '#080b10', padding: '10px', marginBottom: 1 };
        const chartTitle = (t) => <div style={{ color: '#8abcd8', fontWeight: 600, fontSize: 9, marginBottom: 6, paddingLeft: 4, letterSpacing: '2.5px', fontFamily: "'Rajdhani',sans-serif", display:'flex', alignItems:'center', gap:6 }}><span style={{display:'inline-block',width:14,height:1,background:'linear-gradient(90deg,#80b0d0,transparent)'}}></span>{t.toUpperCase()}</div>;
        const TT = ({ active, payload, label }) => active && payload?.length ? (
          <div style={{ background: '#0d1828', border: '1px solid #1e3040', borderRadius: 0, padding: '8px 12px', fontSize: 12 }}>
            <div style={{ color: '#90c0dc', marginBottom: 4, fontFamily: "'Rajdhani',sans-serif", letterSpacing: 1 }}>{label}</div>
            {payload.map((p, i) => <div key={i} style={{ color: p.color }}>{p.name}: {p.value != null && !isNaN(Number(p.value)) ? Number(p.value).toFixed(2) : '-'}</div>)}
          </div>
        ) : null;

        return (
          <div style={{ marginBottom: 8 }}>
            {/* 排1：獲利能力 + 費用率 */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginBottom: 8 }}>
              <div style={{ ...chartStyle, flex: 1, minWidth: 0 }}>
                {chartTitle('📊 營業收入（億）vs 獲利能力（%）')}
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3a4a" />
                    <XAxis dataKey="期別" tick={{ fill: '#aaa', fontSize: 9 }} />
                    <YAxis yAxisId="left" tick={{ fill: '#aaa', fontSize: 9 }} width={40} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#aaa', fontSize: 9 }} unit="%" width={36} />
                    <Tooltip content={<TT />} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
                    <Bar yAxisId="left" dataKey="營業收入" fill="#4C9BB8" opacity={0.8} name="營業收入(億)" />
                    <Line yAxisId="right" type="monotone" dataKey="毛利率" stroke="#d62828" strokeWidth={2} dot={false} name="毛利率%" />
                    <Line yAxisId="right" type="monotone" dataKey="營益率" stroke="#f5c518" strokeWidth={2} dot={false} name="營益率%" />
                    <Line yAxisId="right" type="monotone" dataKey="稅前淨利率" stroke="#ff7f50" strokeWidth={2} dot={false} name="稅前淨利率%" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ ...chartStyle, flex: 1, minWidth: 0 }}>
                {chartTitle('📊 營業收入（億）vs 費用率（%）')}
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3a4a" />
                    <XAxis dataKey="期別" tick={{ fill: '#aaa', fontSize: 9 }} />
                    <YAxis yAxisId="left" tick={{ fill: '#aaa', fontSize: 9 }} width={40} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#aaa', fontSize: 9 }} unit="%" width={36} />
                    <Tooltip content={<TT />} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
                    <Bar yAxisId="left" dataKey="營業收入" fill="#4C9BB8" opacity={0.8} name="營業收入(億)" />
                    <Line yAxisId="right" type="monotone" dataKey="推銷費用率" stroke="#00a14b" strokeWidth={2} dot={false} name="推銷費用率%" />
                    <Line yAxisId="right" type="monotone" dataKey="管理費用率" stroke="#ff9f40" strokeWidth={2} dot={false} name="管理費用率%" />
                    <Line yAxisId="right" type="monotone" dataKey="研發費用率" stroke="#b87fdb" strokeWidth={2} dot={false} name="研發費用率%" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* 排2：不透明資產 + 稅前淨利/營業CF */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginBottom: 8 }}>
              <div style={{ ...chartStyle, flex: 1, minWidth: 0 }}>
                {chartTitle('📊 不透明資產合計（億）vs 財務透明度（%）')}
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3a4a" />
                    <XAxis dataKey="期別" tick={{ fill: '#aaa', fontSize: 9 }} />
                    <YAxis yAxisId="left" tick={{ fill: '#aaa', fontSize: 9 }} width={40} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#aaa', fontSize: 9 }} unit="%" domain={[0, 100]} width={36} />
                    <Tooltip content={<TT />} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
                    <Bar yAxisId="left" dataKey="不透明資產" fill="#00a14b" opacity={0.8} name="不透明資產合計(億)" />
                    <Line yAxisId="right" type="monotone" dataKey="財務透明度" stroke="#d62828" strokeWidth={2} dot={false} name="財務透明度%" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ ...chartStyle, flex: 1, minWidth: 0 }}>
                {chartTitle('📊 稅前淨利 vs 營業活動現金流（億）')}
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3a4a" />
                    <XAxis dataKey="期別" tick={{ fill: '#aaa', fontSize: 9 }} />
                    <YAxis tick={{ fill: '#aaa', fontSize: 9 }} width={44} />
                    <Tooltip content={<TT />} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
                    <Bar dataKey="稅前淨利億" fill="#f5c518" opacity={0.85} name="稅前淨利(億)" />
                    <Bar dataKey="營業CF億" fill="#4C9BB8" opacity={0.85} name="營業活動現金(億)" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* 排3：餘絀現金+有息負債 + 資產結構 */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginBottom: 8 }}>
              <div style={{ ...chartStyle, flex: 1, minWidth: 0 }}>
                {chartTitle('📊 餘絀現金（億）vs 有息負債（億）')}
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3a4a" />
                    <XAxis dataKey="期別" tick={{ fill: '#aaa', fontSize: 9 }} />
                    <YAxis yAxisId="left" tick={{ fill: '#aaa', fontSize: 9 }} width={44} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#aaa', fontSize: 9 }} width={44} />
                    <Tooltip content={<TT />} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
                    <Bar yAxisId="left" dataKey="餘絀CF億" fill="#d62828" opacity={0.85} name="餘絀現金(億)" />
                    <Line yAxisId="right" type="monotone" dataKey="有息負債億" stroke="#00a14b" strokeWidth={2} dot={false} name="有息負債(億)" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ ...chartStyle, flex: 1, minWidth: 0 }}>
                {chartTitle('📊 資產結構（億）vs 營業收入（億）')}
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3a4a" />
                    <XAxis dataKey="期別" tick={{ fill: '#aaa', fontSize: 9 }} />
                    <YAxis yAxisId="left" tick={{ fill: '#aaa', fontSize: 9 }} width={44} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#aaa', fontSize: 9 }} width={44} />
                    <Tooltip content={<TT />} />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
                    <Bar yAxisId="left" dataKey="存貨億" fill="#ff9f40" opacity={0.8} name="存貨(億)" />
                    <Bar yAxisId="left" dataKey="不動產設備億" fill="#4C9BB8" opacity={0.8} name="不動產機器設備(億)" />
                    <Bar yAxisId="left" dataKey="無形資產億" fill="#b87fdb" opacity={0.8} name="無形資產(億)" />
                    <Line yAxisId="right" type="monotone" dataKey="營業收入" stroke="#f5c518" strokeWidth={2} dot={false} name="營業收入(億)" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── 月營收圖表 ── */}
      {mData.length > 0 && (() => {
        const monthlyChart = [...mData].reverse().map(r => ({
          月份: r.period,
          月營收: r.revenue ? +(r.revenue/100000).toFixed(2) : null,
          營收年增率: r.yoy_pct != null ? +Number(r.yoy_pct).toFixed(1) : null,
          近3月累計年增率: r.cum_3m_pct != null ? +Number(r.cum_3m_pct).toFixed(1) : null,
          近12月累計年增率: r.cum_12m_pct != null ? +Number(r.cum_12m_pct).toFixed(1) : null,
        }));

        // [MONTHLY_LASTYEAR_HL] 最新月的去年同月柱子標色
        const _latestP = monthlyChart.length ? String(monthlyChart[monthlyChart.length - 1].月份) : '';
        const _lastYearP = _latestP.length === 6 ? String(Number(_latestP.slice(0, 4)) - 1) + _latestP.slice(4) : '';

        // 月營收標題含最新數值和月增率箭頭
        const m0 = mData[0] || {}, m1 = mData[1] || {};
        const rev0 = m0.revenue ? +(m0.revenue/100000).toFixed(2) : null;
        const rev1 = m1.revenue ? +(m1.revenue/100000).toFixed(2) : null;
        const revArrow = (rev0 && rev1) ? (rev0 > rev1 ? '▲' : rev0 < rev1 ? '▼' : '─') : '';
        const revColor = revArrow === '▲' ? '#d62828' : revArrow === '▼' ? '#00a14b' : '#888';
        const yoy0 = m0.yoy_pct != null ? +Number(m0.yoy_pct).toFixed(1) : null;
        const yoy1 = m1.yoy_pct != null ? +Number(m1.yoy_pct).toFixed(1) : null;
        const yoyArrow = (yoy0 != null && yoy1 != null) ? (yoy0 > yoy1 ? '▲' : yoy0 < yoy1 ? '▼' : '─') : '';
        const yoyColor = yoyArrow === '▲' ? '#d62828' : yoyArrow === '▼' ? '#00a14b' : '#888';
        const mom0 = m0.mom_pct != null ? +Number(m0.mom_pct).toFixed(1) : null;
        const mom1 = m1.mom_pct != null ? +Number(m1.mom_pct).toFixed(1) : null;
        const momArrow = (mom0 != null && mom1 != null) ? (mom0 > mom1 ? '▲' : mom0 < mom1 ? '▼' : '─') : '';
        const momColor = momArrow === '▲' ? '#d62828' : momArrow === '▼' ? '#00a14b' : '#888';
        const c3m0 = m0.cum_3m_pct != null ? +Number(m0.cum_3m_pct).toFixed(1) : null;
        const c3m1 = m1.cum_3m_pct != null ? +Number(m1.cum_3m_pct).toFixed(1) : null;
        const c3mArrow = (c3m0 != null && c3m1 != null) ? (c3m0 > c3m1 ? '▲' : c3m0 < c3m1 ? '▼' : '─') : '';
        const c3mColor = c3mArrow === '▲' ? '#d62828' : c3mArrow === '▼' ? '#00a14b' : '#888';
        const c12m0 = m0.cum_12m_pct != null ? +Number(m0.cum_12m_pct).toFixed(1) : null;
        const c12m1 = m1.cum_12m_pct != null ? +Number(m1.cum_12m_pct).toFixed(1) : null;
        const c12mArrow = (c12m0 != null && c12m1 != null) ? (c12m0 > c12m1 ? '▲' : c12m0 < c12m1 ? '▼' : '─') : '';
        const c12mColor = c12mArrow === '▲' ? '#d62828' : c12mArrow === '▼' ? '#00a14b' : '#888';

        const chartStyle2 = { background: '#060910', borderRadius: 8, padding: '16px 8px', marginBottom: 16 };
        const TT2 = ({ active, payload, label }) => active && payload?.length ? (
          <div style={{ background: '#0d1828', border: '1px solid #1e3040', borderRadius: 0, padding: '8px 12px', fontSize: 12 }}>
            <div style={{ color: '#90c0dc', marginBottom: 4, fontFamily: "'Rajdhani',sans-serif", letterSpacing: 1 }}>{label}</div>
            {payload.map((p, i) => <div key={i} style={{ color: p.color }}>{p.name}: {p.value != null ? p.value : '-'}</div>)}
          </div>
        ) : null;
        return (
          <div style={chartStyle2}>
            <div style={{ paddingLeft: 8, marginBottom: 8 }}>
                  <span style={{ color: '#4C9BB8', fontWeight: 700, fontSize: 13 }}>📅 </span>
                  <span style={{ color: '#4C9BB8', fontWeight: 700, fontSize: 13 }}>月營收 </span>
                  {rev0 != null && <span style={{ fontSize: 12 }}><span style={{ color: '#ddd' }}>{rev0}億 </span><span style={{ color: revColor, fontWeight: 700 }}>{revArrow}</span></span>}
                  <span style={{ color: '#90bcd8', fontSize: 12 }}> ｜ 年增率 </span>
                  {yoy0 != null && <span style={{ fontSize: 12 }}><span style={{ color: '#f5c518' }}>{yoy0}% </span><span style={{ color: yoyColor, fontWeight: 700 }}>{yoyArrow}</span></span>}
                  <span style={{ color: '#90bcd8', fontSize: 12 }}> ｜ 月增率 </span>
                  {mom0 != null && <span style={{ fontSize: 12 }}><span style={{ color: '#7ec8e3' }}>{mom0}% </span><span style={{ color: momColor, fontWeight: 700 }}>{momArrow}</span></span>}
                  <span style={{ color: '#90bcd8', fontSize: 12 }}> ｜ 近3月 </span>
                  {c3m0 != null && <span style={{ fontSize: 12 }}><span style={{ color: '#d62828' }}>{c3m0}% </span><span style={{ color: c3mColor, fontWeight: 700 }}>{c3mArrow}</span></span>}
                  <span style={{ color: '#90bcd8', fontSize: 12 }}> ｜ 近12月 </span>
                  {c12m0 != null && <span style={{ fontSize: 12 }}><span style={{ color: '#ff7f50' }}>{c12m0}% </span><span style={{ color: c12mColor, fontWeight: 700 }}>{c12mArrow}</span></span>}
                </div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={monthlyChart} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a3a4a" />
                <XAxis dataKey="月份" tick={{ fill: '#aaa', fontSize: 9 }} interval={2} />
                <YAxis yAxisId="left" tick={{ fill: '#aaa', fontSize: 9 }} width={44} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#aaa', fontSize: 9 }} unit="%" width={40} />
                <Tooltip content={<TT2 />} />
                <Legend wrapperStyle={{ fontSize: 10, color: '#ccc' }} />
                <Bar yAxisId="left" dataKey="月營收" opacity={0.8} name="月營收(億)">
                  {monthlyChart.map((e, i) => (
                    <Cell key={i} fill={String(e.月份) === _lastYearP ? '#c77dff' : '#4C9BB8'} />
                  ))}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="營收年增率" stroke="#f5c518" strokeWidth={2} dot={false} name="營收年增率%" />
                <Line yAxisId="right" type="monotone" dataKey="近3月累計年增率" stroke="#d62828" strokeWidth={2} dot={false} name="近3月累計%" />
                <Line yAxisId="right" type="monotone" dataKey="近12月累計年增率" stroke="#ff7f50" strokeWidth={2} dot={false} name="近12月累計%" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* ── 籌碼指南針 + ROIC趨勢（左右並排） ── */}
      {chipData.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: '1 1 480px', minWidth: 0, background:'#080b10', border:'1px solid #1a2a3c', padding:'12px 16px' }}>
            {sectionTitle('🧭 籌碼指南針（仙人(大戶)指路線 × 韭菜(散戶)生長線 × 股價）')}
            <ChipCompass data={chipData} />
          </div>
          <div style={{ flex: '1 1 380px', minWidth: 0, background:'#080b10', border:'1px solid #1a2a3c', padding:'12px 16px' }}>
            {sectionTitle('📈 ROIC 趨勢（單季，%）')}
            <ROICChart data={qDataAsc} />
          </div>
        </div>
      )}

      {/* ── 季度損益表 (圖2) ── */}
      {sectionTitle('📊 季度損益表（單位：億 / %）')}
      {renderQTable([
        { label: '營業收入(億)',      key: '營業收入_億',  cf: colorPos },
        { label: '營收年增率(%)',     key: '營收年增率',   cf: colorPos },
        { label: '毛利率(%)',         key: '毛利率',        cf: colorPos },
        { label: '營益率(%)',         key: '營益率',        cf: colorPos },
        { label: '稅前淨利率(%)',     key: '稅前淨利率',    cf: colorPos },
        { label: '稅後淨利率(%)',     key: '稅後淨利率',    cf: colorPos },
        { label: '每股盈餘(元)',      key: '每股盈餘',      cf: colorPos, sep: true },
        { label: '銷貨成本率(%)',     key: null, calc: (q) => q['毛利率'] != null ? +(100 - q['毛利率']).toFixed(2) : null, cf: colorNeg },
        { label: '推銷費用率(%)',     key: '推銷費用率',    cf: colorNeg },
        { label: '管理費用率(%)',     key: '管理費用率',    cf: colorNeg },
        { label: '研發費用率(%)',     key: '研發費用率',    cf: colorNeg },
        { label: '營業費用率(%)',     key: '營業費用率',    cf: colorNeg },
        { label: 'ROIC(單季)', key: 'ROIC',          cf: colorPos },
      ])}

      {/* ── 季度資產負債表 (圖1) ── */}
      {sectionTitle('🏦 季度資產負債表（單位：億）')}
      {renderQTable([
        { label: '約當現金(億)',               key: '現金_億',           cf: colorPos },
        { label: '備供出售金融資產-流動(億)',  key: '備供出售流動_億',    cf: () => '#ccc' },
        { label: '應收帳款(億)',               key: '應收帳款_億',       cf: () => '#ccc' },
        { label: '存貨(億)',                   key: '存貨_億',           cf: () => '#ccc' },
        { label: '備供出售金融資產-非流動(億)',key: '備供出售非流動_億',  cf: () => '#ccc' },
        { label: '採權益法之長期投資(億)',     key: '採權益法投資_億',   cf: () => '#ccc' },
        { label: '不動產機器設備(億)',         key: '不動產廠房設備_億', cf: () => '#ccc' },
        { label: '無形資產(億)',               key: '無形資產_億',        cf: () => '#ccc' },
        { label: '資產總額(億)',               key: '資產總額_億',       cf: colorPos, sep: true },
        { label: '短期借款(億)',               key: '短期借款_億',       cf: colorNeg },
        { label: '應付承兌匯票(億)',           key: '應付承兌匯票_億',    cf: colorNeg },
        { label: '合約負債(億)',               key: '合約負債_億',        cf: () => '#ccc' },
        { label: '應付帳款(億)',               key: '應付帳款_億',       cf: () => '#ccc' },
        { label: '一年到期長期負債(億)',       key: '一年到期長期負債_億', cf: colorNeg },
        { label: '應付公司債(億)',             key: '應付公司債_億',      cf: colorNeg },
        { label: '長期借款(億)',               key: '長期借款_億',        cf: colorNeg },
        { label: '負債總額(億)',               key: '負債總額_億',       cf: colorNeg, sep: true },
        { label: '股本(億)',                   key: '股本_億',           cf: () => '#ccc' },
        { label: '母公司股東權益(億)',         key: '股東權益_億',       cf: colorPos },
        { label: '土地(億)',                   key: '土地_億',            cf: () => '#ccc' },
        { label: '廠房(億)',                   key: '廠房_億',            cf: () => '#ccc' },
        { label: '設備(億)',                   key: '設備_億',            cf: () => '#ccc', sep: true },
        { label: '原料(億)',                   key: '原料_億',            cf: () => '#ccc' },
        { label: '在製品(億)',                 key: '在製品_億',          cf: () => '#ccc' },
        { label: '製成品(億)',                 key: '製成品_億',          cf: () => '#ccc', sep: true },
        { label: '有息負債(億)',               key: '有息負債_億',        cf: colorNeg },
        { label: '財務透明度(%)',              key: '財務透明度',         cf: colorPos, sep: true },
        { label: '應收帳款週轉天數',           key: '應收帳款週轉天數',   cf: () => '#ccc' },
        { label: '存貨週轉天數',               key: '存貨週轉天數',       cf: () => '#ccc' },
      ])}

      {/* ── 季度現金流量表 (圖3) ── */}
      {sectionTitle('💵 季度現金流量表（單位：億）')}
      {renderQTable([
        { label: '稅前淨利(億)',               key: '稅前淨利_億',       cf: colorPos },
        { label: '應收帳款(增)減(億)',         key: '應收帳款增減_億',    cf: colorPos },
        { label: '存貨(增)減(億)',             key: '存貨增減_億',        cf: colorPos },
        { label: '應付帳款增(減)(億)',         key: '應付帳款增減_億',    cf: colorPos },
        { label: '交易目的金融資產(增加)減少(億)', key: '買賣金融資產_億', cf: colorPos },
        { label: '修正式營業活動現金流入(出)(億)', calc: q => { const a = q['營業CF_億'] ?? null; const b = q['買賣金融資產_億'] ?? null; return (a !== null && b !== null) ? parseFloat((a - b).toFixed(2)) : a; }, cf: colorPos, sep: true },
        { label: '購置不動產設備(億)',         key: '購置不動產設備_億', cf: colorPos },
        { label: '處分不動產設備(億)',         key: '處分不動產_億',     cf: colorPos },
        { label: '修正式投資活動現金流入(出)(億)', calc: q => { const op = q['營業CF_億'] ?? null; const fa = q['買賣金融資產_億'] ?? null; const cap = q['購置不動產設備_億'] ?? null; const dis = q['處分不動產_億'] ?? null; const modOp = (op !== null && fa !== null) ? op - fa : op; if (modOp === null) return null; const c = cap ?? 0; const d = dis ?? 0; return parseFloat((modOp + c + d).toFixed(2)); }, cf: colorPos, sep: true },
        { label: '支付現金股利(億)',           key: '支付股利_億',       cf: colorPos },
        { label: '短期借款增(減)(億)',         key: '短期借款增減_億',    cf: colorPos },
        { label: '應付票券增(減)(億)',         key: '應付票券增減_億',    cf: colorPos },
        { label: '發行公司債(億)',             key: '發行公司債_億',      cf: colorPos },
        { label: '舉借長期借款(億)',           key: '舉借長期借款_億',    cf: colorPos },
        { label: '償還長期借款(億)',           key: '償還長期借款_億',    cf: colorPos },
        { label: '現金增(減)資(億)',           key: '現金增資_億',        cf: colorPos },
        { label: '籌資活動現金流入(出)(億)',   key: '籌資CF_億',         cf: colorPos, sep: true },
        { label: '修正式自由現金流量(億)',      key: '自由CF_億',         cf: colorPos },
        { label: '餘絀現金流量(億)',           key: '餘絀CF_億',         cf: colorPos },
        { label: '盈餘含金量(營業CF/稅前淨利%)', key: '盈餘含金量',     cf: colorPos },
      ])}

      {/* ── 年度財務 (圖4) ── */}
      {sectionTitle('📈 年度財務（單位：億）')}
      {renderATable([
        { label: '資產總額(億)',               key: 'total_assets',      cf: colorPos },
        { label: '負債總額(億)',               key: 'total_liab',        cf: colorNeg },
        { label: '母公司股東權益總額(億)',     key: 'total_equity',      cf: colorPos },
        { label: '股本(億)',                   key: 'common_stock',      cf: () => '#ccc', sep: true },
        { label: '營業收入(億)',               key: 'revenue',           cf: () => '#ccc' },
        { label: '銷貨成本(億)',               key: 'cost',              cf: colorNeg },
        { label: '營業毛利(億)',               key: 'gross_profit',      cf: colorPos },
        { label: '營業費用(億)',               key: 'op_expense',        cf: colorNeg },
        { label: '營業利益(億)',               key: 'op_income',         cf: colorPos },
        { label: '營外收支(億)',               key: 'non_op_income',     cf: colorPos },
        { label: '稅前淨利(億)',               key: 'pretax_income',     cf: colorPos },
        { label: '母公司淨利(億)',             key: 'net_income',        cf: colorPos },
        { label: '每股盈餘(元)',               key: 'eps',               cf: colorPos, sep: true },
        { label: '營業活動現金流入(出)(億)',   key: 'op_cashflow',       cf: colorPos },
        { label: '投資活動現金流入(出)(億)',   key: 'inv_cashflow',      cf: colorPos },
        { label: '籌資活動現金流入(出)(億)',   key: 'fin_cashflow',      cf: colorPos },
        { label: '購置不動產及設備(億)',        key: 'capex',         cf: colorPos },
        { label: '處分不動產及設備(億)',        key: 'ppe_disposal',      cf: colorPos },
        { label: '無形資產(商譽)(增)減(億)',   key: 'intangible_change', cf: colorPos },
        { label: '支付現金股利(億)',           key: 'dividend_paid',     cf: colorPos, sep: true },
        { label: '修正式營業活動現金流量(億)',  calc: a => { const op = a['op_cashflow'] ?? null; const fa = a['fa_trading'] ?? null; return (op !== null && fa !== null) ? parseFloat((op - fa).toFixed(2)) : op; }, cf: colorPos },
        { label: '修正式自由現金流量(億)',      key: 'free_cashflow',     cf: colorPos },
        { label: '餘絀現金流量(億)',           key: 'surplus_cf',        cf: colorPos },
        { label: '業外收支/營收(%)',           key: 'non_op_rev_ratio',  cf: colorPos },
      ])}

      {/* ── 月營收 ── */}
      {sectionTitle('📅 月營收')}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#070a0f' }}>
              <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, background: '#070a0f', minWidth: 220 }}>項目</th>
              {mData.map(r => <th key={r.period} style={th}>{r.period}</th>)}
            </tr>
          </thead>
          <tbody>
            {[
              { label: '月營收(億)',               fn: (r) => fmtN(Number(r.revenue)/100000), cf: () => '#ccc' },
              { label: '與上月營收成長(%)',         fn: (r) => r.mom_pct != null ? Number(r.mom_pct) : null, cf: colorPos },
              { label: '營收年增率(%)',             fn: (r) => r.yoy_pct != null ? Number(r.yoy_pct) : null, cf: colorPos },
              { label: '累計營收年增率(%)',         fn: (r) => r.cum_yoy_pct != null ? Number(r.cum_yoy_pct) : null, cf: colorPos },
              { label: '近3個月累計營收年增率(%)',  fn: (r) => r.cum_3m_pct != null ? Number(r.cum_3m_pct) : null, cf: colorPos },
              { label: '近12個月累計營收年增率(%)', fn: (r) => r.cum_12m_pct != null ? Number(r.cum_12m_pct) : null, cf: colorPos },
            ].map((row, ri) => (
              <tr key={row.label} style={{ background: ri % 2 === 0 ? '#080b10' : '#070a0e' }}>
                <td style={{ ...td, textAlign: 'left', color: '#8ab8d0', fontWeight: 500, position: 'sticky', left: 0, zIndex: 1, background: '#080b10', ...(row.sep ? { borderBottom: '2px solid #ffffff' } : {}) }}>{row.label}</td>
                {mData.map((r, i) => {
                  const val = row.fn(r, i);
                  const display = val == null ? '-' : (typeof val === 'string' ? val : val.toFixed(2) + (row.label.includes('億') ? '' : '%'));
                  return <td key={r.period} style={{ ...td, color: val == null ? '#666' : row.cf(val) }}>{display}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── 股利 ── */}
      {sectionTitle('💰 股利')}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#070a0f' }}>
              <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, background: '#070a0f', minWidth: 220 }}>項目</th>
              {dividend.map(r => <th key={r.year} style={th}>{r.year}</th>)}
            </tr>
          </thead>
          <tbody>
            {[
              { label: '現金股利',  key: 'cash_dividend',    cf: colorPos },
              { label: 'EPS',       key: 'eps_prev_year',    cf: colorPos },
              { label: '發放率(%)', key: 'payout_ratio_pct', cf: () => '#ccc' },
            ].map((row, i) => (
              <tr key={row.key} style={{ background: i % 2 === 0 ? '#080b10' : '#070a0e' }}>
                <td style={{ ...td, textAlign: 'left', color: '#8ab8d0', fontWeight: 500, position: 'sticky', left: 0, zIndex: 1, background: '#080b10', ...(row.sep ? { borderBottom: '2px solid #ffffff' } : {}) }}>{row.label}</td>
                {dividend.map(r => (
                  <td key={r.year} style={{ ...td, color: row.cf(r[row.key]) }}>{fmt(r[row.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── 籌碼分析 ── */}
      {shareholding.length > 0 && (
        <div style={{ margin: '0 0 32px 0' }}>
          {sectionTitle('📊 籌碼分析（集保股權分散）')}
          <ShareholdingChart data={shareholding} closePrice={closePrice} priceHistory={priceHistory} />
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #2a3a4a', color: '#ddd', whiteSpace: 'nowrap', background: '#070a0f' };
const thSticky = { ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, background: '#070a0f', minWidth: 120 };
const td = { padding: '7px 12px', textAlign: 'right', borderBottom: '1px solid #1e2a3a', whiteSpace: 'nowrap' };
const tdSticky = { ...td, textAlign: 'left', position: 'sticky', left: 0, zIndex: 1, background: '#080b10', fontWeight: 600, color: '#ddd' };
const tdStickyAlt = { ...tdSticky, background: '#070a0e' };




// ── 籌碼指南針 ─────────────────────────────────────
function ChipCompass({ data }) {
  if (!data || data.length < 4) return null;
  const d = data.filter((_,i) => i % 4 === 0 || i === data.length-1);

  const maxPrice  = Math.max(...d.map(x=>x.high||x.close||0));
  const minPrice  = Math.min(...d.map(x=>x.low||x.close||0).filter(v=>v>0));

  const fmtDate = s => s?.slice(0,7) || '';
  const fmtK = v => v >= 10000 ? `${(v/10000).toFixed(0)}萬張` : `${v.toFixed(0)}張`;

  return (
    <div style={{ width:'100%', fontSize:11 }}>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={d} margin={{top:8,right:60,left:10,bottom:8}}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3c" vertical={false}/>
          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{fill:'#4a6878',fontSize:9}}
            interval={Math.floor(d.length/8)} tickLine={false}/>
          <YAxis yAxisId="left" tick={{fill:'#f0a500',fontSize:9}}
            tickFormatter={fmtK} domain={['auto','auto']}
            label={{value:'大戶(張)',angle:-90,position:'insideLeft',fill:'#f0a500',fontSize:9,dx:-4}}/>
          <YAxis yAxisId="right" orientation="right" tick={{fill:'#7ab0cc',fontSize:9}}
            domain={['auto','auto']}
            label={{value:'散戶(戶)',angle:90,position:'insideRight',fill:'#7ab0cc',fontSize:9,dx:4}}/>
          <YAxis yAxisId="price" orientation="right" tick={{fill:'#999',fontSize:9}}
            domain={[minPrice*0.97, maxPrice*1.03]} width={50}/>
          <Tooltip
            contentStyle={{background:'#080b10',border:'1px solid #1a2a3c',fontSize:11}}
            labelFormatter={s=>s?.slice(0,10)}
            formatter={(v,name)=>{
              if(name==='仙人(大戶)指路線') return [fmtK(v),'大戶持股'];
              if(name==='韭菜(散戶)生長線') return [(v||0).toLocaleString()+'戶','散戶戶數'];
              if(name==='價格') return [`${v}元`,'收盤價'];
              return [v,name];
            }}
          />
          <Legend wrapperStyle={{fontSize:11,paddingTop:4}}/>
          <Line yAxisId="left"  dataKey="big_shares_k"  name="仙人(大戶)指路線" stroke="#f0a500" strokeWidth={2} dot={false}/>
          <Line yAxisId="right" dataKey="retail_holders" name="韭菜(散戶)生長線" stroke="#d62828" strokeWidth={2} dot={false}/>
          <Line yAxisId="price" dataKey="close" name="價格" stroke="#90c0dc" strokeWidth={1.5} dot={false} strokeDasharray="4 2"/>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── ROIC 季度趨勢圖 ────────────────────────────────
function ROICChart({ data }) {
  const d = (data || []).filter(q => q['ROIC'] !== '' && q['ROIC'] != null);
  if (d.length < 2) return <div style={{ color: '#4a6878', fontSize: 12, padding: '20px 0' }}>資料季數不足，尚無法繪製ROIC趨勢</div>;

  return (
    <div style={{ width: '100%', fontSize: 11 }}>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={d} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3c" vertical={false} />
          <XAxis dataKey="期別" tick={{ fill: '#4a6878', fontSize: 9 }} interval={Math.floor(d.length / 10)} />
          <YAxis yAxisId="roic" tick={{ fill: '#3ed888', fontSize: 9 }} unit="%" width={40} />
          <YAxis yAxisId="inv" orientation="right" tick={{ fill: '#7a8fa8', fontSize: 9 }} width={50}
            label={{ value: '存貨(億)', angle: 90, position: 'insideRight', fill: '#7a8fa8', fontSize: 9, dx: 4 }} />
          <Tooltip
            contentStyle={{ background: '#080b10', border: '1px solid #1a2a3c', fontSize: 11 }}
            formatter={(v, name) => {
              if (name === 'ROIC(單季)') return [`${v}%`, 'ROIC(單季)'];
              if (name === '存貨(億)') return [v, '存貨(億)'];
              return [v, name];
            }}
          />
          <Legend wrapperStyle={{fontSize:11,paddingTop:4}}/>
          <Bar yAxisId="inv" dataKey="存貨_億" name="存貨(億)" fill="#3a5068" opacity={0.6} />
          <Line yAxisId="roic" type="monotone" dataKey="ROIC" name="ROIC(單季)" stroke="#3ed888" strokeWidth={2} dot={false} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── K 線圖 ──────────────────────────────────────────
function KLineChart({ data }) {
  if (!data || data.length < 20) return null;
  const raw = data.filter(d => d.close);
  if (raw.length < 20) return null;

  // 計算 MA20 / MA60
  const withMA = raw.map((d, i) => {
    const s20 = raw.slice(Math.max(0,i-19),i+1);
    const s60 = raw.slice(Math.max(0,i-59),i+1);
    return {
      ...d,
      ma20: +(s20.reduce((s,x)=>s+(x.close||0),0)/s20.length).toFixed(2),
      ma60: +(s60.reduce((s,x)=>s+(x.close||0),0)/s60.length).toFixed(2),
    };
  });

  // 最新數據和前一日比較
  const last  = withMA[withMA.length - 1];
  const prev  = withMA[withMA.length - 2] || last;
  const last2 = withMA[withMA.length - 3] || prev; // 前前日MA用
  const priceUp  = last.close >= prev.close;
  const ma20Up   = last.ma20  >= prev.ma20;
  const ma60Up   = last.ma60  >= prev.ma60;
  const volUp    = (last.volume || 0) >= (prev.volume || 0);
  const fmtVol   = v => Math.round((v || 0) / 1000).toLocaleString() + '張';

  const W=900, H=300, VH=50, PL=50, PR=16, PT=20, PB=24;
  const cW=W-PL-PR, cH=H-PT-PB-VH-6, n=withMA.length;
  const bW=Math.max(1, Math.floor(cW/n)-1);
  const cx=i=>PL+(i+0.5)*cW/n;
  const highs=withMA.map(d=>d.high||d.close), lows=withMA.map(d=>d.low||d.close);
  const maxP=Math.max(...highs), minP=Math.min(...lows), rng=maxP-minP||1;
  const maxV=Math.max(...withMA.map(d=>d.volume||0))||1;
  const maxI=highs.indexOf(maxP), minI=lows.indexOf(minP);
  const py=p=>PT+cH-(p-minP)/rng*cH;
  const vy=v=>H-PB-(v/maxV)*VH;

  const gridPrices=[0,0.25,0.5,0.75,1].map(f=>+(minP+rng*f).toFixed(1));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:'auto',display:'block'}}>
      {/* 左上角統計面板 */}
      {[
        { label: `價格 ${last.close?.toFixed(1)}`,     color: priceUp?'#d62828':'#00a14b', arrow: priceUp?'▲':'▼', sub: '' },
        { label: `MA20 ${last.ma20}`,         color: ma20Up?'#f5c518':'#aaa',    arrow: ma20Up?'↑':'↓',  sub: '' },
        { label: `MA60 ${last.ma60}`,         color: ma60Up?'#c084fc':'#aaa',    arrow: ma60Up?'↑':'↓',  sub: '' },
        { label: `成交量 ${fmtVol(last.volume||0)}`, color: volUp?'#4C9BB8':'#aaa',  arrow: volUp?'↑':'↓',   sub: '' },
      ].map((item, i) => (
        <g key={i}>
          <text x={PL+4 + i*145} y={PT+14} fontSize={11} fill={item.color} fontWeight={700} fontFamily="monospace">
            {item.arrow} {item.label}
          </text>
        </g>
      ))}
      {/* 格線 */}
      {gridPrices.map((p,i)=>{
        const yp=py(p);
        return <g key={i}>
          <line x1={PL} x2={W-PR} y1={yp} y2={yp} stroke="#1a2a3a" strokeWidth={0.5}/>
          <text x={PL-4} y={yp+4} textAnchor="end" fontSize={9} fill="#4a6878">{p}</text>
        </g>;
      })}
      {/* K棒 */}
      {withMA.map((d,i)=>{
        const o=d.open||d.close,h=d.high||d.close,l=d.low||d.close,c=d.close;
        const up=c>=o, col=up?'#d62828':'#00a14b';
        const x=cx(i), yH=py(h), yL=py(l), yO=py(o), yC=py(c);
        const bTop=Math.min(yO,yC), bH=Math.max(1,Math.abs(yO-yC));
        return <g key={i}>
          <line x1={x} x2={x} y1={yH} y2={yL} stroke={col} strokeWidth={0.8}/>
          <rect x={x-bW/2} y={bTop} width={bW} height={bH} fill={col} opacity={0.85}/>
        </g>;
      })}
      {/* MA20 */}
      <polyline points={withMA.map((d,i)=>`${cx(i)},${py(d.ma20)}`).join(' ')} fill="none" stroke="#f5c518" strokeWidth={1.3} opacity={0.9}/>
      {/* MA60 */}
      <polyline points={withMA.map((d,i)=>`${cx(i)},${py(d.ma60)}`).join(' ')} fill="none" stroke="#c060f0" strokeWidth={1.3} opacity={0.9}/>
      {/* 最高點 */}
      <circle cx={cx(maxI)} cy={py(maxP)} r={3.5} fill="#f0a500"/>
      <text x={cx(maxI)+(maxI>n*0.7?-38:6)} y={py(maxP)-5} fontSize={9} fill="#f0a500">▲{maxP}</text>
      {/* 最低點 */}
      <circle cx={cx(minI)} cy={py(minP)} r={3.5} fill="#7ab0cc"/>
      <text x={cx(minI)+(minI>n*0.7?-38:6)} y={py(minP)+13} fontSize={9} fill="#7ab0cc">▼{minP}</text>
      {/* 成交量 */}
      {withMA.map((d,i)=>{
        const col=(d.close||0)>=(d.open||d.close)?'#ff4444':'#22cc66';
        const vh=((d.volume||0)/maxV)*VH;
        return <rect key={i} x={cx(i)-bW/2} y={H-PB-vh} width={bW} height={vh} fill={col} opacity={0.85}/>;
      })}
      {/* X軸標籤（每季一次）*/}
      {withMA.filter((_,i,a)=>{
        if(i===0) return true;
        const m=withMA[i].date?.slice(5,7);
        const pm=withMA[i-1].date?.slice(5,7);
        return m!==pm && ['01','04','07','10'].includes(m);
      }).map(d=>{
        const i=withMA.findIndex(w=>w.date===d.date);
        return <text key={d.date} x={cx(i)} y={H-PB+10} textAnchor="middle" fontSize={8} fill="#4a6878">{d.date?.slice(0,7)}</text>;
      })}
    </svg>
  );
}

function ShareholdingChart({ data, closePrice, priceHistory = {} }) {
  const LEVEL_RANGES = {
    1:  { min: 1,       max: 999 },      2:  { min: 1000,    max: 5000 },
    3:  { min: 5001,    max: 10000 },    4:  { min: 10001,   max: 15000 },
    5:  { min: 15001,   max: 20000 },    6:  { min: 20001,   max: 30000 },
    7:  { min: 30001,   max: 40000 },    8:  { min: 40001,   max: 50000 },
    9:  { min: 50001,   max: 100000 },   10: { min: 100001,  max: 200000 },
    11: { min: 200001,  max: 400000 },   12: { min: 400001,  max: 600000 },
    13: { min: 600001,  max: 800000 },   14: { min: 800001,  max: 1000000 },
    15: { min: 1000001, max: Infinity },
  };
  const price = closePrice ? Number(closePrice.close) : null;

  // 大股東：持股市值≥5000萬；若所有級別都達不到（股價太低），則用最高級別（15）
  const getBigLevels = p => {
    if (!p) return [15];
    const lvs = Object.entries(LEVEL_RANGES)
      .filter(([,{min}]) => min * p >= 50_000_000)
      .map(([lv]) => Number(lv));
    return lvs.length > 0 ? lvs : [15]; // 股價太低時用最高級別
  };

  // 散戶：持股市值<100萬，門檻落在哪個級別就包含整個級別（無條件進位），但上限為1000張（level 14）
  const getRetailLevels = p => {
    if (!p) return [1];
    const threshold = 1_000_000 / p; // 持股股數門檻
    const lvs = Object.entries(LEVEL_RANGES)
      .filter(([lv, {min}]) => min <= threshold && Number(lv) <= 14) // 上限 level 14（1000張）
      .map(([lv]) => Number(lv));
    return lvs.length > 0 ? lvs : [1];
  };
  const bigLevels = getBigLevels(price);
  const retailLevels = getRetailLevels(price);
  const byDate = {};
  data.forEach(r => { if (!byDate[r.date]) byDate[r.date] = {}; byDate[r.date][r.level] = r; });
  const dates = Object.keys(byDate).sort().reverse().slice(0, 52);
  const latest = byDate[dates[0]] || {};
  const agg = (day, lvs) => {
    let h = 0, p = 0;
    lvs.forEach(lv => { if (day[lv]) { h += day[lv].holders||0; p += parseFloat(day[lv].pct||0); } });
    return { holders: h, pct: p.toFixed(2) };
  };
  const bigHolder = agg(latest, bigLevels);
  const retail    = agg(latest, retailLevels);
  const total     = latest[17] || {};
  const bigLabel    = '大股東';
  const retailLabel = '散戶';
  const arrowStr   = (a, b, k) => !b ? '' : Number(a[k]) > Number(b[k]) ? ' ↑' : Number(a[k]) < Number(b[k]) ? ' ↓' : '';
  const arrowClr   = (a, b, k) => !b ? '#aaa' : Number(a[k]) > Number(b[k]) ? '#d62828' : Number(a[k]) < Number(b[k]) ? '#00a14b' : '#aaa';
  return (
    <div>
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        {[
          { label: '大股東',       value: bigHolder.holders.toLocaleString(), color:'#f0a500' },
          { label: '大股東持股比%', value: bigHolder.pct ? `${bigHolder.pct}%` : '-', color:'#d62828' },
          { label: '散戶',    value: retail.holders.toLocaleString(), color:'#7ab0cc' },
          { label: '總股東人數',    value: (total.holders||0).toLocaleString(), color:'#aaa' },
        ].map(c => (
          <div key={c.label} style={{ background:'#080c14', border:'1px solid #1e2a3a', borderRadius:8, padding:'12px 18px', minWidth:170, flex:1 }}>
            <div style={{ color:'#7ab0cc', fontSize:11, marginBottom:4 }}>{c.label}</div>
            <div style={{ color:c.color, fontSize:20, fontWeight:700 }}>{c.value}</div>
          </div>
        ))}
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead><tr style={{ background:'#070a0f' }}>
            <th style={{ ...th, textAlign:'left', minWidth:100 }}>日期</th>
            <th style={th}>收盤價</th>
            <th style={th}>大股東人數</th>
            <th style={th}>大股東持股%</th>
            <th style={th}>散戶人數</th>
            <th style={th}>散戶持股%</th>
            <th style={th}>總股東人數</th>
          </tr></thead>
          <tbody>
            {dates.map((d, i) => {
              const row = byDate[d]||{};
              const big = agg(row, bigLevels), ret = agg(row, retailLevels), tot = row[17]||{};
              const prev = i < dates.length-1 ? dates[i+1] : null;
              const pb = prev ? agg(byDate[prev]||{}, bigLevels) : null;
              const pr = prev ? agg(byDate[prev]||{}, retailLevels) : null;
              const ds = d.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3');
              const dsKey = d.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
              const pks = Object.keys(priceHistory).filter(k => k <= dsKey).sort();
              const cp = pks.length ? priceHistory[pks[pks.length-1]] : null;
              return (
                <tr key={d} style={{ background: i%2===0?'#080b10':'#070a0e' }}>
                  <td style={{ ...td, textAlign:'left', color:'#aaa' }}>{ds}</td>
                  <td style={{ ...td, color:'#c0d8ea', fontFamily:"'JetBrains Mono',monospace" }}>{cp ? Number(cp).toLocaleString() : '-'}</td>
                  <td style={{ ...td, fontWeight:i===0?700:400 }}>
                    <span style={{ color:'#f0a500' }}>{big.holders.toLocaleString()}</span>
                    <span style={{ color:arrowClr(big,pb,'holders'), fontSize:11 }}>{arrowStr(big,pb,'holders')}</span>
                  </td>
                  <td style={td}>
                    <span style={{ color:'#d62828' }}>{big.pct?`${big.pct}%`:'-'}</span>
                    <span style={{ color:arrowClr(big,pb,'pct'), fontSize:11 }}>{arrowStr(big,pb,'pct')}</span>
                  </td>
                  <td style={td}>
                    <span style={{ color:'#7ab0cc' }}>{ret.holders.toLocaleString()}</span>
                    <span style={{ color:arrowClr(ret,pr,'holders'), fontSize:11 }}>{arrowStr(ret,pr,'holders')}</span>
                  </td>
                  <td style={{ ...td, color:'#9090b0' }}>{ret.pct?`${ret.pct}%`:'-'}</td>
                  <td style={{ ...td, color:'#aaa' }}>{(tot.holders||0).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
